import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import { CaretLeft, CaretRight, Plus } from "@phosphor-icons/react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["S","M","T","W","T","F","S"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [entriesByDate, setEntriesByDate] = useState({});
  const [loading, setLoading] = useState(true);

  const y = cursor.getFullYear();
  const m = cursor.getMonth();

  const load = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      const r = await api.get("/entries", { params: { year: y, month: m + 1 } });
      const grouped = {};
      r.data.forEach((e) => {
        const key = (e.entry_date || "").slice(0, 10);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(e);
      });
      setEntriesByDate(grouped);
    } catch {
      navigate("/", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [y, m]);

  const grid = useMemo(() => {
    const first = new Date(y, m, 1);
    const startDayOfWeek = first.getDay(); // 0=Sun
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const today = ymd(new Date());
    const cells = [];
    for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(y, m, day);
      const key = ymd(d);
      cells.push({
        day, key,
        isToday: key === today,
        isFuture: d > new Date(),
        entries: entriesByDate[key] || [],
      });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [y, m, entriesByDate]);

  const stats = useMemo(() => {
    const daysWithEntry = Object.keys(entriesByDate).length;
    const now = new Date();
    const daysSoFar =
      y === now.getFullYear() && m === now.getMonth()
        ? now.getDate()
        : new Date(y, m + 1, 0).getDate();
    return { daysWithEntry, daysSoFar };
  }, [entriesByDate, y, m]);

  const jumpTo = (cell) => {
    if (!cell || cell.isFuture) return;
    if (cell.entries.length > 0) {
      navigate(`/entry/${cell.entries[0].entry_id}`);
    } else {
      // No entry — start a new one for that date via URL param on dashboard? Just go to dashboard for now.
      navigate("/dashboard");
    }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen paper-grain" data-testid="calendar-page">
      <TopNav user={user} onLogout={async () => { await api.post("/auth/logout"); navigate("/"); }} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Calendar</p>
        <div className="flex items-baseline justify-between mt-3 mb-8 flex-wrap gap-4">
          <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tighter">
            {MONTHS[m]} <span className="italic text-accent">{y}</span>
          </h1>
          <div className="flex items-center gap-2">
            <button
              data-testid="cal-prev"
              onClick={() => setCursor(new Date(y, m - 1, 1))}
              className="p-2 rounded-full border border-border hover:border-primary transition-colors"
              aria-label="Previous month"
            >
              <CaretLeft size={16} />
            </button>
            <button
              data-testid="cal-today"
              onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }}
              className="text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 rounded-full border border-border hover:border-primary transition-colors"
            >
              Today
            </button>
            <button
              data-testid="cal-next"
              onClick={() => setCursor(new Date(y, m + 1, 1))}
              className="p-2 rounded-full border border-border hover:border-primary transition-colors"
              aria-label="Next month"
            >
              <CaretRight size={16} />
            </button>
          </div>
        </div>

        <div className="border border-border rounded-lg bg-card p-4 sm:p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4" data-testid="cal-stats">
            {stats.daysWithEntry} of {stats.daysSoFar} days written
          </p>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="text-xs uppercase tracking-[0.2em] text-muted-foreground text-center py-2">{w}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
            {grid.map((c, i) => {
              if (!c) return <div key={i} className="aspect-square" />;
              const has = c.entries.length > 0;
              return (
                <button
                  key={c.key}
                  data-testid={`cal-day-${c.key}`}
                  onClick={() => jumpTo(c)}
                  disabled={c.isFuture}
                  className={`aspect-square rounded-lg border transition-colors flex flex-col items-center justify-center text-sm relative ${
                    c.isFuture
                      ? "border-transparent text-muted-foreground/40 cursor-not-allowed"
                      : has
                        ? "border-primary bg-primary/10 hover:bg-primary/20 text-foreground"
                        : "border-border/40 text-muted-foreground hover:border-primary"
                  } ${c.isToday ? "ring-2 ring-accent ring-offset-2 ring-offset-card" : ""}`}
                >
                  <span className="font-serif text-lg leading-none">{c.day}</span>
                  {has && <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-primary/60" /> Written
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full border-2 border-border" /> Empty
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full ring-2 ring-accent" /> Today
          </span>
        </div>

        <div className="mt-10">
          <Button
            data-testid="cal-new-entry"
            onClick={() => navigate("/dashboard")}
            className="rounded-full bg-primary text-primary-foreground"
          >
            <Plus size={16} className="mr-2" /> New entry
          </Button>
        </div>
      </main>
    </div>
  );
}
